import { FormControl, FormHelperText, TextField } from '@mui/material';
import {
  Controller,
  useFormContext,
  type FieldPathByValue,
  type RegisterOptions,
} from 'react-hook-form';
import { NumericFormat } from 'react-number-format';

import type { PropertyAdEditorFormValues } from '../../../types/propertyAdEditor';

import { LabelComponent } from '../../uiComponents/LabelComponent';
import { AiReviewFieldNotice, useAiListingReview } from '../AiListingReview';
import styles from '../PropertyAdEditor.module.scss';

type EditorNumericFieldName = FieldPathByValue<PropertyAdEditorFormValues, number | null>;

interface EditorNumericFieldProps<TName extends EditorNumericFieldName> {
  ariaLabel?: string;
  disabled?: boolean;
  label: string;
  name: TName;
  placeholder?: string;
  required?: boolean;
  rules?: RegisterOptions<PropertyAdEditorFormValues, TName>;
}

export const EditorNumericField = <TName extends EditorNumericFieldName>({
  ariaLabel,
  disabled = false,
  label,
  name,
  placeholder,
  required = false,
  rules,
}: EditorNumericFieldProps<TName>) => {
  const { control } = useFormContext<PropertyAdEditorFormValues>();
  const aiReview = useAiListingReview();
  const errorId = `${name}-error`;

  return (
    <Controller<PropertyAdEditorFormValues, TName>
      control={control}
      name={name}
      rules={rules}
      render={({ field, fieldState }) => (
        <FormControl className={styles.editorNumericField} error={fieldState.invalid}>
          <LabelComponent isRequired={required} label={label} />
          <NumericFormat
            allowNegative={false}
            customInput={TextField}
            decimalScale={0}
            disabled={disabled}
            error={fieldState.invalid}
            fullWidth
            inputRef={field.ref}
            name={field.name}
            placeholder={placeholder}
            thousandSeparator
            value={field.value ?? ''}
            onBlur={field.onBlur}
            onValueChange={({ floatValue }, source) => {
              field.onChange(floatValue ?? null);
              if (source.source === 'event') {
                aiReview?.resolveField(name as keyof PropertyAdEditorFormValues);
              }
            }}
            inputProps={{
              'aria-describedby': fieldState.error ? errorId : undefined,
              'aria-label': ariaLabel ?? label,
              'aria-invalid': fieldState.invalid,
            }}
          />
          {fieldState.error?.message && (
            <FormHelperText id={errorId}>{fieldState.error.message}</FormHelperText>
          )}
          <AiReviewFieldNotice target={name as keyof PropertyAdEditorFormValues} />
        </FormControl>
      )}
    />
  );
};
